import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Inbox, CalendarClock, Zap, CheckCircle2, XCircle, Euro, TrendingUp, ClipboardList, Sparkles, ShieldAlert, ShieldCheck, RefreshCw, Mail, Database } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [health, setHealth] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = () => api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  const loadHealth = () => api.get("/admin/health").then((r) => setHealth(r.data)).catch(() => {});
  useEffect(() => { load(); loadHealth(); }, []);

  const runCheck = async () => {
    setChecking(true);
    try {
      const r = await api.post("/admin/health/check");
      toast[r.data.ok ? "success" : "error"](r.data.ok ? "Tutto funziona correttamente" : "Rilevato un problema tecnico");
      loadHealth();
    } catch (e) { toast.error("Errore durante la verifica"); }
    setChecking(false);
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/admin/seed-demo");
      toast.success(`Dati demo creati (${r.data.requests_created} richieste)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setSeeding(false);
  };

  if (!data) return <div className="text-gray-400">Caricamento…</div>;

  const cards = [
    { label: "Richieste totali", value: data.total, icon: Inbox, color: "#0052FF" },
    { label: "Ricevute oggi", value: data.today, icon: ClipboardList, color: "#8B5CF6" },
    { label: "Contatti immediati", value: data.immediate, icon: Zap, color: "#EF4444" },
    { label: "Appuntamenti programmati", value: data.scheduled, icon: CalendarClock, color: "#F59E0B" },
    { label: "Da lavorare", value: data.to_work, icon: ClipboardList, color: "#0EA5E9" },
    { label: "Attivazioni", value: data.activations, icon: CheckCircle2, color: "#10B981" },
    { label: "Annullate / non interessati", value: data.cancelled, icon: XCircle, color: "#6B7280" },
    { label: "Fatturato potenziale", value: `${data.potential_revenue.toLocaleString("it-IT")} €`, icon: Euro, color: "#111111" },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Panoramica delle segnalazioni Dopopay</p>
        </div>
        <Button onClick={seed} disabled={seeding} data-testid="seed-demo-btn" variant="outline" className="rounded-xl border-gray-300">
          <Sparkles className="w-4 h-4 mr-2" /> {seeding ? "Creazione…" : "Genera dati demo"}
        </Button>
      </div>

      {health?.latest && !health.latest.ok && (
        <div data-testid="health-banner" className="bg-red-50 border-2 border-red-300 rounded-2xl p-5 flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
            <ShieldAlert className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1">
            <p className="font-display font-bold text-red-700">Attenzione: rilevato un problema tecnico</p>
            <ul className="mt-1 text-sm text-red-700 list-disc pl-5 space-y-0.5">
              {!health.latest.checks?.email?.ok && <li>L'invio delle email non funziona: le segnalazioni non arrivano via email a {health.alert_email}.</li>}
              {!health.latest.checks?.database?.ok && <li>Il database non è raggiungibile.</li>}
            </ul>
            <p className="text-xs text-red-500 mt-2">Ultimo controllo: {(health.latest.at || "").replace("T", " ").slice(0, 16)}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={`kpi-${c.label}`} className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-shadow duration-200">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${c.color}15` }}>
              <c.icon className="w-5 h-5" style={{ color: c.color }} />
            </div>
            <p className="font-display text-2xl font-bold">{c.value}</p>
            <p className="text-sm text-gray-500 mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="system-status-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <h2 className="font-display font-semibold text-lg flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#E6B800]" /> Stato del sistema
          </h2>
          <Button onClick={runCheck} disabled={checking} data-testid="run-health-check-btn" variant="outline" className="rounded-xl border-gray-300">
            <RefreshCw className={`w-4 h-4 mr-2 ${checking ? "animate-spin" : ""}`} /> {checking ? "Verifica…" : "Verifica ora"}
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <StatusRow icon={Mail} label="Invio email (notifiche lead)" ok={health?.latest?.checks?.email?.ok}
            detail={health?.latest?.checks?.email?.detail} />
          <StatusRow icon={Database} label="Database" ok={health?.latest?.checks?.database?.ok}
            detail={health?.latest?.checks?.database?.detail} />
        </div>
        <p className="text-xs text-gray-400 mt-4">
          Controllo automatico ogni giorno alle 08:00 (fuso Europe/Rome). Se un problema riguarda l'email, l'avviso appare qui in dashboard; per gli altri problemi viene inviata una mail a {health?.alert_email || "l.spatola@monetito.it"}.
          {health?.latest?.at && ` · Ultimo controllo: ${health.latest.at.replace("T", " ").slice(0, 16)}`}
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h2 className="font-display font-semibold text-lg flex items-center gap-2 mb-5">
          <TrendingUp className="w-5 h-5 text-[#E6B800]" /> Segnalazioni per agente
        </h2>
        {data.per_agent.length === 0 ? (
          <p className="text-gray-400 text-sm">Nessun dato disponibile.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.per_agent} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
              <XAxis dataKey="agent" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip cursor={{ fill: "#f5f5f5" }} />
              <Bar dataKey="reports" name="Segnalazioni" radius={[6, 6, 0, 0]}>
                {data.per_agent.map((_, i) => <Cell key={i} fill="#FFCC00" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function StatusRow({ icon: Icon, label, ok, detail }) {
  const known = ok !== undefined && ok !== null;
  return (
    <div className={`rounded-xl border p-4 flex items-start gap-3 ${!known ? "border-gray-200 bg-gray-50" : ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
      <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${!known ? "text-gray-400" : ok ? "text-green-600" : "text-red-600"}`} />
      <div className="min-w-0">
        <p className="font-medium text-sm">{label}</p>
        <p className={`text-xs mt-0.5 ${!known ? "text-gray-400" : ok ? "text-green-700" : "text-red-700"}`}>
          {!known ? "In attesa del primo controllo" : ok ? "Funzionante" : "Non funzionante"}
        </p>
        {known && !ok && detail && <p className="text-[11px] text-red-500 mt-1 break-words">{detail}</p>}
      </div>
    </div>
  );
}
